import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  ShoppingBag,
  Mail,
  Package,
  Banknote,
  RefreshCcw,
  ChevronDown,
  ChevronRight,
  Trash2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatRSD = (n) => new Intl.NumberFormat("sr-RS").format(n || 0);
const formatDate = (iso) =>
  new Date(iso).toLocaleString("sr-RS", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const STATUS_LABEL = {
  new: { label: "Nova", color: "bg-[#c5a572] text-[#0a0a0a]" },
  processing: { label: "U obradi", color: "bg-blue-500 text-white" },
  delivered: { label: "Isporučena", color: "bg-green-600 text-white" },
  cancelled: { label: "Otkazana", color: "bg-red-500 text-white" },
};

const PAY_LABEL = { cash: "Keš", card: "Kartica", ips: "IPS" };

export default function AdminDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null); // { type: 'order'|'contact', id, label }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c, s] = await Promise.all([
        axios.get(`${API}/admin/orders`, { headers: authHeaders }),
        axios.get(`${API}/admin/contacts`, { headers: authHeaders }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders }),
      ]);
      setOrders(o.data || []);
      setContacts(c.data || []);
      setStats(s.data || null);
    } catch (e) {
      toast.error("Greška pri učitavanju");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${id}`, { status }, { headers: authHeaders });
      setOrders((arr) => arr.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success("Status promenjen");
    } catch (e) {
      toast.error("Greška");
    }
  };

  const deleteOrder = async (id) => {
    try {
      await axios.delete(`${API}/admin/orders/${id}`, { headers: authHeaders });
      setOrders((arr) => arr.filter((o) => o.id !== id));
      setStats((s) => (s ? { ...s, orders: Math.max(0, (s.orders || 1) - 1) } : s));
      toast.success("Porudžbina obrisana");
    } catch (e) {
      toast.error("Greška pri brisanju");
    }
  };

  const deleteContact = async (id) => {
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: authHeaders });
      setContacts((arr) => arr.filter((c) => c.id !== id));
      setStats((s) => (s ? { ...s, contacts: Math.max(0, (s.contacts || 1) - 1) } : s));
      toast.success("Poruka obrisana");
    } catch (e) {
      toast.error("Greška pri brisanju");
    }
  };

  const confirmDelete = async () => {
    if (!confirmDel) return;
    if (confirmDel.type === "order") await deleteOrder(confirmDel.id);
    else if (confirmDel.type === "contact") await deleteContact(confirmDel.id);
    setConfirmDel(null);
  };

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-white/5 sticky top-0 bg-[#0a0a0a]/90 backdrop-blur z-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif text-lg tracking-[0.18em]">STANIMIROVIĆ</span>
            <span className="text-[10px] tracking-[0.34em] text-white/40 uppercase">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="text-white/60 hover:text-white p-2"
              aria-label="refresh"
            >
              <RefreshCcw size={16} />
            </button>
            <span className="hidden sm:block text-[11px] text-white/50">{user?.email}</span>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 text-white/65 hover:text-[#c5a572] border border-white/15 hover:border-[#c5a572] px-3 py-1.5 rounded-full text-[11px] tracking-[0.28em] uppercase transition-colors"
            >
              <LogOut size={13} /> Odjava
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard
            icon={<ShoppingBag size={16} />}
            label="Sve porudžbine"
            value={stats?.orders ?? "–"}
          />
          <StatCard
            icon={<Package size={16} />}
            label="Nove porudžbine"
            value={stats?.new_orders ?? "–"}
            highlight
          />
          <StatCard
            icon={<Mail size={16} />}
            label="Kontakt poruke"
            value={stats?.contacts ?? "–"}
          />
          <StatCard
            icon={<Banknote size={16} />}
            label="Ukupan promet"
            value={stats ? `${formatRSD(stats.total_revenue_rsd)} RSD` : "–"}
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-white/5">
          <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testid="tab-orders">
            Porudžbine ({orders.length})
          </TabBtn>
          <TabBtn active={tab === "contacts"} onClick={() => setTab("contacts")} testid="tab-contacts">
            Poruke ({contacts.length})
          </TabBtn>
        </div>

        {loading && (
          <div className="py-12 text-center text-white/40 text-sm">Učitavanje...</div>
        )}

        {!loading && tab === "orders" && (
          <div data-testid="orders-list" className="space-y-3">
            {orders.length === 0 && (
              <div className="py-16 text-center text-white/40">Nema porudžbina.</div>
            )}
            {orders.map((o) => {
              const open = openRow === o.id;
              const st = STATUS_LABEL[o.status] || STATUS_LABEL.new;
              return (
                <div
                  key={o.id}
                  data-testid={`order-row-${o.id}`}
                  className="border border-white/10 bg-[#0d0d0d]"
                >
                  <button
                    onClick={() => setOpenRow(open ? null : o.id)}
                    className="w-full text-left p-4 lg:p-5 flex items-center gap-4 hover:bg-white/[0.02] transition-colors"
                  >
                    {open ? (
                      <ChevronDown size={16} className="text-white/40 flex-shrink-0" />
                    ) : (
                      <ChevronRight size={16} className="text-white/40 flex-shrink-0" />
                    )}
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-3 items-center">
                      <div className="font-serif text-base">{o.name}</div>
                      <div className="text-[12px] text-white/55 hidden md:block">{o.phone}</div>
                      <div className="text-[12px] text-white/55 hidden md:block">{o.city}</div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">
                        {formatDate(o.created_at)}
                      </div>
                      <div className="flex items-center gap-3 justify-end">
                        <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${st.color}`}>
                          {st.label}
                        </span>
                        <span className="font-serif text-lg text-[#c5a572] whitespace-nowrap">
                          {formatRSD(o.total_rsd)} RSD
                        </span>
                      </div>
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-white/5 p-5 lg:p-6 bg-[#080808]">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label>Kontakt</Label>
                          <Row k="Ime" v={o.name} />
                          <Row k="Telefon" v={<a className="text-[#c5a572]" href={`tel:${o.phone}`}>{o.phone}</a>} />
                          <Row k="Email" v={<a className="text-[#c5a572]" href={`mailto:${o.email}`}>{o.email}</a>} />
                          <Row k="Adresa" v={`${o.address}, ${o.city}`} />
                          <Row k="Plaćanje" v={PAY_LABEL[o.payment_method] || o.payment_method} />
                          {o.note && <Row k="Napomena" v={o.note} />}
                        </div>
                        <div>
                          <Label>Artikli</Label>
                          <ul className="space-y-2">
                            {(o.items || []).map((it, i) => (
                              <li key={i} className="flex justify-between text-sm">
                                <span>
                                  {it.name} <span className="text-white/45">× {it.quantity}</span>
                                </span>
                                <span className="text-[#c5a572]">
                                  {formatRSD(it.price_rsd * it.quantity)} RSD
                                </span>
                              </li>
                            ))}
                          </ul>
                          <div className="mt-4 pt-3 border-t border-white/10 flex justify-between font-serif text-lg">
                            <span>Ukupno</span>
                            <span className="text-[#c5a572]">{formatRSD(o.total_rsd)} RSD</span>
                          </div>
                          <Label className="mt-6">Status</Label>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(STATUS_LABEL).map(([k, info]) => (
                              <button
                                key={k}
                                data-testid={`status-${o.id}-${k}`}
                                onClick={() => updateStatus(o.id, k)}
                                className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 transition-colors ${
                                  o.status === k
                                    ? info.color
                                    : "border border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                                }`}
                              >
                                {info.label}
                              </button>
                            ))}
                          </div>
                          <div className="mt-8 pt-4 border-t border-white/5 flex justify-end">
                            <button
                              data-testid={`delete-order-${o.id}`}
                              onClick={() =>
                                setConfirmDel({ type: "order", id: o.id, label: `${o.name} • ${formatRSD(o.total_rsd)} RSD` })
                              }
                              className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-500/60 px-3 py-1.5 rounded-full text-[10px] tracking-[0.28em] uppercase transition-colors"
                            >
                              <Trash2 size={12} /> Obriši porudžbinu
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && tab === "contacts" && (
          <div data-testid="contacts-list" className="space-y-3">
            {contacts.length === 0 && (
              <div className="py-16 text-center text-white/40">Nema poruka.</div>
            )}
            {contacts.map((c) => (
              <div
                key={c.id}
                data-testid={`contact-row-${c.id}`}
                className="border border-white/10 bg-[#0d0d0d] p-5 relative"
              >
                <button
                  data-testid={`delete-contact-${c.id}`}
                  onClick={() => setConfirmDel({ type: "contact", id: c.id, label: `${c.name} • ${c.email}` })}
                  className="absolute top-4 right-4 text-white/30 hover:text-red-400 transition-colors p-1.5"
                  aria-label="delete"
                >
                  <Trash2 size={14} />
                </button>
                <div className="flex flex-wrap items-center gap-3 justify-between mb-3 pr-8">
                  <div className="font-serif text-lg">{c.name}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">
                    {formatDate(c.created_at)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 text-[12px] text-white/60 mb-3">
                  <a className="hover:text-[#c5a572]" href={`mailto:${c.email}`}>
                    {c.email}
                  </a>
                  {c.phone && (
                    <a className="hover:text-[#c5a572]" href={`tel:${c.phone}`}>
                      {c.phone}
                    </a>
                  )}
                  {c.subject && (
                    <span className="text-white/80 font-medium">— {c.subject}</span>
                  )}
                </div>
                <p className="text-white/80 font-light whitespace-pre-wrap">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent
          data-testid="delete-confirm-dialog"
          className="bg-[#0a0a0a] border border-white/10 text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-white">
              {confirmDel?.type === "contact" ? "Obriši poruku?" : "Obriši porudžbinu?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/55 font-light">
              {confirmDel?.label}
              <br />
              Ova radnja je trajna i ne može se vratiti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel"
              className="bg-transparent border-white/15 text-white/70 hover:bg-white/5 hover:text-white"
            >
              Otkaži
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Obriši
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ icon, label, value, highlight }) {
  return (
    <div
      className={`border border-white/10 p-5 ${
        highlight ? "bg-[#c5a572]/8" : "bg-[#0d0d0d]"
      }`}
    >
      <div className="flex items-center gap-2 text-white/45">
        {icon}
        <span className="text-[10px] tracking-[0.32em] uppercase">{label}</span>
      </div>
      <div className="mt-3 font-serif text-3xl text-[#c5a572]">{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-3 text-[11px] tracking-[0.28em] uppercase transition-all border-b-2 -mb-px ${
        active ? "text-[#c5a572] border-[#c5a572]" : "text-white/50 border-transparent hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function Label({ children, className = "" }) {
  return (
    <div className={`text-[10px] tracking-[0.32em] uppercase text-white/40 mb-3 ${className}`}>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 text-sm border-b border-white/5 last:border-0">
      <span className="text-white/50 text-[12px]">{k}</span>
      <span className="text-white/90 text-right">{v}</span>
    </div>
  );
}
